import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import {
  FilterParams,
  PaginationParams,
  ResourcesService,
} from '../_services/resources.service';
import { CommonModule, DatePipe } from '@angular/common';
import { AuthService } from '../_services/auth.service';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { createDefaultFilter, ResourceFilter } from './resourceFilter';
import { ResourceDto, ResourceResDto } from '../_dtos/resourceResDto';
import { ResourceTypes } from '../_enums/resourceTypes';
import { ResourceStatus } from '../_enums/resourceStatus';
import { FilterFormData } from '../_models/filterFormData';
import { debounceTime, distinctUntilChanged, filter, Subscription } from 'rxjs';
import { TableDataService } from '../_services/table-data.service';
import { PaginationState } from './paginationState';
import { TextCardComponent } from '../shared/text-card/text-card.component';
import { StatusBarComponent } from '../shared/status-bar/status-bar.component';

@Component({
  selector: 'app-my-resources',
  imports: [
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormField,
    MatLabel,
    MatInputModule,
    MatSelectModule,
    MatButton,
    MatTooltip,
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatIconButton,
    TextCardComponent,
    StatusBarComponent,
  ],
  templateUrl: './all-resources.component.html',
  standalone: true,
  styleUrl: './all-resources.component.css',
  providers: [
    DatePipe,
    {
      provide: MatPaginatorIntl,
      useFactory: CustomPaginator,
      deps: [TranslateService],
    },
  ],
})
export class AllResourcesComponent implements OnInit, AfterViewInit, OnDestroy {
  public tableDataSource = new MatTableDataSource<ResourceDto>([]);
  public tableColumns: string[] = [
    'resourceType',
    'displayName',
    'primaryOwner',
    'secondaryOwner',
    'resourceStatus',
    'endOfLife',
  ];
  private filters: ResourceFilter = createDefaultFilter();
  public userPrincipalName!: string;
  public filterMenu!: FormGroup;
  public selectedRow: ResourceDto | null = null;
  public dataIsLoaded: boolean = false;

  private paginationState: PaginationState = {
    pageSize: 10,
    totalCount: 0,
    currentPageIndex: 0,
  };

  private resourceUpdateSubscription!: Subscription;
  private resourceDeleteSubscription!: Subscription;
  private searchSubscription?: Subscription;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private resourcesService: ResourcesService,
    private authService: AuthService,
    private tableDataService: TableDataService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.initializeForm();
    this.userPrincipalName = this.authService.getUsername();
    this.setupDebouncedSearch();
  }

  ngAfterViewInit() {
    this.updatePaginatorState();
  }

  ngOnDestroy() {
    if (this.resourceUpdateSubscription)
      this.resourceUpdateSubscription.unsubscribe();
    if (this.resourceDeleteSubscription)
      this.resourceDeleteSubscription.unsubscribe();
    if (this.searchSubscription) this.searchSubscription.unsubscribe();
  }

  private initializeForm() {
    this.filterMenu = new FormGroup<FilterFormData>({
      search: new FormControl(),
      resourceType: new FormControl(),
      resourceStatus: new FormControl(),
    });
  }

  private initializeTable(data: ResourceDto[]) {
    this.tableDataSource = new MatTableDataSource<ResourceDto>(data);

    this.changeDetectorRef.detectChanges();
    if (this.sort) this.tableDataSource.sort = this.sort;

    this.tableDataSource.sortingDataAccessor = (
      item: ResourceDto,
      property: string,
    ): string => {
      switch (property) {
        case 'displayName':
          return item.displayName.toLocaleLowerCase();
        default:
          return '';
      }
    };
  }

  private updatePaginatorState() {
    if (this.paginator) {
      this.paginator.length = this.paginationState.totalCount;
      this.paginator.pageSize = this.paginationState.pageSize;
      this.paginator.pageIndex = this.paginationState.currentPageIndex;
    }
  }

  private handleApiResponse(data: ResourceResDto) {
    this.initializeTable(data.resources);

    // Update pagination state
    this.paginationState.totalCount = data.totalCount || 0;
    this.updatePaginatorState();
  }

  filterSearch() {
    this.filters.search =
      this.filterMenu.get('search')?.value?.trim().toLowerCase() || '';
    if (this.filters.search !== '') this.updateFilter();
    else this.clearFilter();
  }

  updateFilter() {
    const filters: FilterParams = {};
    filters.searchText = this.filters.search;

    const offset =
      this.paginationState.currentPageIndex * this.paginationState.pageSize;
    const pagination: PaginationParams = {
      pageSize: this.paginationState.pageSize,
      startIndex: offset > 0 ? offset : undefined,
    };

    this.resourcesService.getAllResources(filters, pagination).subscribe({
      next: (data) => {
        this.handleApiResponse(data);
      },
    });
    this.dataIsLoaded = true;
  }

  clearFilter() {
    this.filters = { search: '' };
    this.filterMenu.reset(this.filterMenu);

    this.paginationState.currentPageIndex = 0;
    const emptyResponse: ResourceResDto = {
      resources: [],
      nextPageToken: 0,
      totalCount: 0,
    };

    this.dataIsLoaded = false;
    this.handleApiResponse(emptyResponse);
  }

  onPageChange(event: PageEvent) {
    const targetPageIndex = event.pageIndex;
    const newPageSize = event.pageSize;
    const oldPageSize = this.paginationState.pageSize;

    // Update pagination state
    this.paginationState.pageSize = newPageSize;

    // If page size changed, we need to recalculate and reload from page 0
    if (newPageSize !== oldPageSize) this.paginationState.currentPageIndex = 0;
    else this.paginationState.currentPageIndex = targetPageIndex;

    if (this.filters.search !== '') this.updateFilter();
  }

  private setupDebouncedSearch() {
    this.searchSubscription = this.filterMenu
      .get('search')
      ?.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter((value) => value !== null),
      )
      .subscribe(() => {
        this.filterSearch();
      });
  }

  clickedRow(row: ResourceDto): void {
    this.selectedRow = this.selectedRow === row ? null : row;
  }

  openInNewTab(url: string) {
    window.open(url, '_blank');
  }

  protected readonly ResourceTypes = ResourceTypes;
  protected readonly ResourceStatus = ResourceStatus;
}

export function CustomPaginator(translate: TranslateService): MatPaginatorIntl {
  const paginatorIntl = new MatPaginatorIntl();

  const translateLabels = () => {
    paginatorIntl.itemsPerPageLabel = translate.instant(
      'PAGINATOR.ITEMS_PER_PAGE',
    );
    paginatorIntl.nextPageLabel = translate.instant('PAGINATOR.NEXT_PAGE');
    paginatorIntl.previousPageLabel = translate.instant(
      'PAGINATOR.PREVIOUS_PAGE',
    );
    paginatorIntl.firstPageLabel = translate.instant('PAGINATOR.FIRST_PAGE');
    paginatorIntl.lastPageLabel = translate.instant('PAGINATOR.LAST_PAGE');
    paginatorIntl.getRangeLabel = (
      page: number,
      pageSize: number,
      length: number,
    ): string => {
      if (length === 0 || pageSize === 0) {
        return `0 ${translate.instant('PAGINATOR.OF')} ${length}`;
      }

      const startIndex = page * pageSize;
      const endIndex =
        startIndex < length
          ? Math.min(startIndex + pageSize, length)
          : startIndex + pageSize;

      return `${startIndex + 1} – ${endIndex} ${translate.instant('PAGINATOR.OF')} ${length}`;
    };
  };

  translate.onLangChange.subscribe(() => {
    translateLabels();
    paginatorIntl.changes.next();
  });

  translateLabels();

  return paginatorIntl;
}
